import { z } from 'zod';
import { paymentConfigSchema, paymentConfigDtoSchema } from '../../PaymentConfig.js';
import { Services } from '../services/Services.js';
import { servicesProviderPricingSchema, servicesProviderPricingDtoSchema } from '../ServicesProviderPricing.js';
import { serviceConfigSchema } from './SharedServiceConfig.js';

// ============================================================================
// Enums
// ============================================================================
const SaleAssignmentServices = [
    Services.TITLING,
    Services.REGISTRATION,
    Services.EXTERNAL_TAX,
    Services.EXTERNAL_FEES,
];
const PrimaryMarketAreaType = {
    ZIP_CODES: 'ZIP_CODES',
    RADIUS: 'RADIUS',
};
const primaryMarketAreaTypeValues = [
    PrimaryMarketAreaType.ZIP_CODES,
    PrimaryMarketAreaType.RADIUS,
];
const PrimaryMarketAreaTypeSchema = z.enum(primaryMarketAreaTypeValues);
const AssignmentAlgorithm = {
    ALWAYS_SELF: 'always_self',
    ALWAYS_EKHO: 'always_ekho',
    SELF_FOR_IN_STATE: 'self_for_in_state', // if not in-state, then use ekho svp for out of state buyers
};
const assignmentAlgorithmValues = [
    AssignmentAlgorithm.ALWAYS_SELF,
    AssignmentAlgorithm.ALWAYS_EKHO,
    AssignmentAlgorithm.SELF_FOR_IN_STATE,
];
const AssignmentAlgorithmSchema = z.enum(assignmentAlgorithmValues);
// ============================================================================
// Pricing Schemas
// ============================================================================
const saleServicePricingSchema = servicesProviderPricingSchema;
const saleServicePricingDtoSchema = servicesProviderPricingDtoSchema;
// ============================================================================
// Nested Schemas
// ============================================================================
const primaryMarketAreaSchema = z.object({
    type: PrimaryMarketAreaTypeSchema,
    zipCodes: z.array(z.string()).optional(), // for ZIP_CODES type
    radius: z
        .object({
        miles: z.number(),
        center: z.object({
            lat: z.number(),
            lng: z.number(),
        }),
    })
        .optional(), // for RADIUS type
});
const saleAssignmentServicesSchema = z.object({
    [Services.TITLING]: AssignmentAlgorithmSchema.optional(),
    [Services.REGISTRATION]: AssignmentAlgorithmSchema.optional(),
    [Services.EXTERNAL_TAX]: AssignmentAlgorithmSchema.optional(),
    [Services.EXTERNAL_FEES]: AssignmentAlgorithmSchema.optional(),
});
const saleServiceCheckoutSchema = z.object({
    paymentConfig: paymentConfigSchema.optional(), // allows overrides of the payment config when this service provider is the seller
    assignment: z
        .object({
        // controls the assignment algorithm for each service
        services: saleAssignmentServicesSchema,
    })
        .optional(),
});
const saleServiceFinancingSchema = z.object({
    frfId: z.string(),
});
// ============================================================================
// Core Schema
// ============================================================================
const saleServiceConfigSchema = serviceConfigSchema.extend({
    licenseNumber: z.string(), // dealer license number or equivalent
    primaryMarketArea: primaryMarketAreaSchema.optional(),
    pricing: z.array(saleServicePricingSchema).optional(), // Doc Fee pricing
    checkout: saleServiceCheckoutSchema.optional(),
    financing: saleServiceFinancingSchema,
    isPerformingFulfillment: z.boolean().optional(), // true if this services provider is performing fulfillment for this sale
});
// ============================================================================
// DTO Schema
// ============================================================================
const primaryMarketAreaDtoSchema = z.object({
    type: PrimaryMarketAreaTypeSchema,
    zipCodes: z.array(z.string()).optional(),
    radius: z
        .object({
        miles: z.coerce.number(),
        center: z.object({
            lat: z.coerce.number(),
            lng: z.coerce.number(),
        }),
    })
        .optional(),
});
const saleAssignmentServicesDtoSchema = z.object({
    [Services.TITLING]: AssignmentAlgorithmSchema.optional(),
    [Services.REGISTRATION]: AssignmentAlgorithmSchema.optional(),
    [Services.EXTERNAL_TAX]: AssignmentAlgorithmSchema.optional(),
    [Services.EXTERNAL_FEES]: AssignmentAlgorithmSchema.optional(),
});
const saleServiceCheckoutDtoSchema = z.object({
    paymentConfig: paymentConfigDtoSchema.optional(),
    assignment: z
        .object({
        services: saleAssignmentServicesDtoSchema,
    })
        .optional(),
});
const saleServiceFinancingDtoSchema = z.object({
    frfId: z.string(),
});
const saleServiceConfigDtoSchema = serviceConfigSchema.extend({
    licenseNumber: z.string(),
    primaryMarketArea: primaryMarketAreaDtoSchema.optional(),
    pricing: z.array(saleServicePricingDtoSchema).optional(),
    checkout: saleServiceCheckoutDtoSchema.optional(),
    financing: saleServiceFinancingDtoSchema.optional(),
    isPerformingFulfillment: z.boolean().optional(),
});

export { AssignmentAlgorithm, AssignmentAlgorithmSchema, PrimaryMarketAreaType, PrimaryMarketAreaTypeSchema, SaleAssignmentServices, assignmentAlgorithmValues, primaryMarketAreaDtoSchema, primaryMarketAreaSchema, primaryMarketAreaTypeValues, saleAssignmentServicesDtoSchema, saleAssignmentServicesSchema, saleServiceCheckoutDtoSchema, saleServiceCheckoutSchema, saleServiceConfigDtoSchema, saleServiceConfigSchema, saleServiceFinancingDtoSchema, saleServiceFinancingSchema, saleServicePricingDtoSchema, saleServicePricingSchema };
//# sourceMappingURL=SaleServiceConfig.js.map
